import React, { useEffect, useState, useContext, useCallback } from "react";
import Joyride, { STATUS, ACTIONS, EVENTS } from "react-joyride";
import QboxmailApi from "../qboxmailApi";
import { t } from "i18next";
import TranslatedString from "Components/TranslatedString";
import Button from "Components/button/Button";
import { Icon, close, globeColor, teamMembersColor, etliveColor } from "Icons/index";
import { TourContext } from "ReactContext/index";

const api = new QboxmailApi({ useCookie: true });

const TOUR_KEY = "intro";
const TOUR_ACTIONS = {
  VIDEO_PLAY: "VIDEO_PLAY",
  VIDEO_END: "VIDEO_COMPLETED",
  TOUR_ENDED: "TOUR_ENDED",
  TOUR_ABORTED: "TOUR_ABORTED",
  TOUR_STARTED: "TOUR_STARTED",
};

const TourVideo = ({ onPlay, onEnded, src, stepKey }) => {
  const [activeControls, setActiveControls] = useState(false);

  return (
    <div className="max-w-2xl p-0 shadow-lg rounded-xl">
      <video
        controls={activeControls}
        onLoadedData={() => setActiveControls(true)}
        className="max-w-xl rounded-xl"
        onEnded={() => onEnded(stepKey)}
        onPlay={() => onPlay(stepKey)}
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
};

const TourTooltip = ({
  continuous,
  index,
  step: { title, description, listItems, content, buttonTextKey = "button.next", stepKey, titleIcon },
  tooltipProps,
  size,
  uuid,
  onTooltipClose,
  onBack,
  onNext,
}) => {
  const onVideoEnded = async (stepKey) => {
    const data = {
      uuid: uuid,
      action: TOUR_ACTIONS.VIDEO_END,
      tourKey: TOUR_KEY,
      stepKey,
    };

    await api.sendTourData(data);
  };

  const onVideoPlay = async (stepKey) => {
    const data = {
      uuid: uuid,
      action: TOUR_ACTIONS.VIDEO_PLAY,
      tourKey: TOUR_KEY,
      stepKey,
    };

    await api.sendTourData(data);
  };

  return (
    <div {...tooltipProps} className="bg-white">
      {title && (
        <div className="bg-[--bg-d5] px-4 py-2 flex justify-between">
          <div className="font-semibold text-[--text] text-base flex items-center">
            {titleIcon && <Icon svg={titleIcon} className="mr-2" />}
            <TranslatedString>{title}</TranslatedString>
          </div>
          <Icon
            className="cursor-pointer"
            svg={close}
            width="1rem"
            height="1rem"
            onClick={() => onTooltipClose(stepKey)}
          />
        </div>
      )}
      <div className="px-4 py-2 text-[--text-a60] text-sm">
        {description && <TranslatedString>{description}</TranslatedString>}
        {listItems && (
          <ul className="grid grid-flow-col grid-rows-3 ml-5">
            {listItems.map((itemKey) => (
              <li key={itemKey} className="text-[--accent] flex items-center">
                <div className="rounded-full w-2 h-2 bg-[--accent]" />
                <span className="ml-3 text-[--text-a60]">
                  <TranslatedString>{itemKey}</TranslatedString>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="p-4">{React.cloneElement(content, { stepKey, onEnded: onVideoEnded, onPlay: onVideoPlay })}</div>
      <div className="w-full bg-[--bg] flex justify-between py-7 px-4 items-center">
        <div className="text-[--text-a60]">
          {index + 1} / {size}
        </div>
        <div className="flex justify-end">
          {index > 0 && (
            <Button className="mr-2" color="text" onClick={onBack}>
              button.back
            </Button>
          )}
          {continuous && <Button onClick={onNext}>{buttonTextKey}</Button>}
        </div>
      </div>
    </div>
  );
};

const Tour = ({ isSidebarOpen }) => {
  const STEPS_WITH_SIDEBAR = [
    {
      disableBeacon: true,
      target: "body",
      title: "tour.step1.title",
      description: "tour.step1.description",
      stepKey: "introduction",
      content: <TourVideo src={BASE_URL_PUBLIC_ASSETS + "/onboarding-videos/mod01-newDomain.mp4"} />,
      placement: "center",
    },
    {
      disableBeacon: true,
      target: ".step-management",
      title: "tour.step2.title",
      titleIcon: globeColor,
      description: "tour.step2.description",
      listItems: ["tour.step2.listItem1", "tour.step2.listItem2", "tour.step2.listItem3", "tour.step2.listItem4", "tour.step2.listItem5", "tour.step2.listItem6"],
      content: <TourVideo src={BASE_URL_PUBLIC_ASSETS + "/onboarding-videos/mod02-domainSecurity.mp4"} />,
      placement: "right-start",
      stepKey: "management",
    },
    {
      disableBeacon: true,
      target: ".step-roles",
      title: "tour.step3.title",
      titleIcon: teamMembersColor,
      description: "tour.step3.description",
      listItems: ["tour.step3.listItem1", "tour.step3.listItem2", "tour.step3.listItem3"],
      content: <TourVideo src={BASE_URL_PUBLIC_ASSETS + "/onboarding-videos/mod03-teamMembers.mp4"} />,
      placement: "right",
      stepKey: "roles",
    },
    {
      disableBeacon: true,
      target: ".step-analysis",
      title: "tour.step4.title",
      titleIcon: etliveColor,
      description: "tour.step4.description",
      listItems: ["tour.step4.listItem1", "tour.step4.listItem2", "tour.step4.listItem3"],
      content: <TourVideo src={BASE_URL_PUBLIC_ASSETS + "/onboarding-videos/mod04-tracemail.mp4"} />,
      placement: "right-end",
      stepKey: "analysis",
    },
    {
      disableBeacon: true,
      target: ".tour-start-container",
      content: <>{t("tour.step5.content")}</>,
      buttonTextKey: "button.understood",
      stepKey: "tour_location_suggestion",
    },
  ];

  const STEPS_WITHOUT_SIDEBAR = [
    {
      disableBeacon: true,
      target: "body",
      title: "tour.step1.title",
      description: "tour.step1.description",
      stepKey: "introduction",
      content: <TourVideo src={BASE_URL_PUBLIC_ASSETS + "/onboarding-videos/mod01-newDomain.mp4"} />,
      placement: "center",
    },
    {
      disableBeacon: true,
      target: ".tour-start-container",
      content: <>{t("tour.step5.content")}</>,
      buttonTextKey: "button.understood",
      stepKey: "tour_location_suggestion",
    },
  ];
  const { canRunTour, runTour: run, setRunTour: setRun } = useContext(TourContext);
  const [stepIndex, setStepIndex] = useState(0);
  const [aborted, setAborted] = useState(false);
  const [sessionUUID, setSessionUUID] = useState(null);
  const [steps, setSteps] = useState(STEPS_WITH_SIDEBAR);
  //needed to handle joyride index issue
  const [switchStop, setSwitchStop] = useState(false);

  useEffect(() => {
    //needed to handle joyride index issue
    setSwitchStop(true);

    //handle sidebar presence
    if (!isSidebarOpen) {
      setSteps(STEPS_WITHOUT_SIDEBAR);
    } else {
      setSteps(STEPS_WITH_SIDEBAR);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidebarOpen]);

  //handle joyride index issue on steps update
  useEffect(() => {
    if (stepIndex > 0) {
      setStepIndex(STEPS_WITHOUT_SIDEBAR.length - 1);
    }
    setSwitchStop(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  const tourCallback = ({ action, index, status, type, step }) => {
    if (action === "start" && index === 0) {
      const session_uuid = crypto.randomUUID();
      setSessionUUID(session_uuid);

      api.sendTourData({
        uuid: session_uuid,
        tourKey: TOUR_KEY,
        stepKey: step.stepKey,
        action: TOUR_ACTIONS.TOUR_STARTED,
      });
    }

    if (status === STATUS.FINISHED) {
      if (!aborted) {
        api.sendTourData({
          uuid: sessionUUID,
          tourKey: TOUR_KEY,
          stepKey: step.stepKey,
          action: TOUR_ACTIONS.TOUR_ENDED,
        });
      }

      setStepIndex(0);
      setRun(false);
      setAborted(false);
      setSessionUUID(null);
    } else if ([EVENTS.TARGET_NOT_FOUND].includes(type)) {
      const stepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(stepIndex);
    }
  };

  const TooltipMemo = useCallback(
    (props) => (
      <TourTooltip
        {...props}
        uuid={sessionUUID}
        onTooltipClose={(stepKey) => {
          api.sendTourData({
            uuid: sessionUUID,
            tourKey: TOUR_KEY,
            stepKey: stepKey,
            action: TOUR_ACTIONS.TOUR_ABORTED,
          });
          setAborted(true);
          setStepIndex(steps.length - 1);
        }}
        onBack={() => setStepIndex((prev) => prev - 1)}
        onNext={() => setStepIndex((prev) => prev + 1)}
      />
    ),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionUUID]
  );

  return (
    <Joyride
      callback={tourCallback}
      run={Boolean(canRunTour && run && !switchStop)}
      steps={steps}
      styles={{
        options: {
          zIndex: 1000,
        },
      }}
      floaterProps={{
        styles: {
          floaterWithAnimation: false,
        },
      }}
      continuous
      scrollToFirstStep
      tooltipComponent={TooltipMemo}
      stepIndex={stepIndex}
      disableCloseOnEsc
      disableOverlayClose
    />
  );
};

export default Tour;
